import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import { toast } from "sonner";
import { Plus, Check, RefreshCw } from "lucide-react";
import MoneyCandidates from "../components/MoneyCandidates";

const KIND_LABELS = {
  OWED_TO_ME: "MONEY OWED TO ME",
  INVOICE: "INVOICES OUT",
  DEAL_CLOSING: "DEALS CLOSING THIS WEEK",
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

const tone = (item) => {
  if (item.status === "PAID" || item.status === "WON") return "green";
  if (item.status === "OVERDUE" || item.status === "LOST") return "red";
  const d = daysUntil(item.due_date);
  if (d == null) return "neutral";
  if (d < 0) return "red";
  if (d <= 7) return "amber";
  return "green";
};

const TONE_STYLE = {
  green: { border: "border-l-[#22c55e]", text: "text-[#22c55e]", bg: "bg-[rgba(34,197,94,0.05)]" },
  amber: { border: "border-l-[#F5A623]", text: "text-[#F5A623]", bg: "bg-[rgba(245,166,35,0.05)]" },
  red: { border: "border-l-[#ef4444]", text: "text-[#ef4444]", bg: "bg-[rgba(239,68,68,0.06)]" },
  neutral: { border: "border-l-white/10", text: "text-[#A0A5B5]", bg: "" },
};

const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

const MoneySection = ({ kind, items, onToggle }) => {
  const rows = items.filter((i) => i.kind === kind);
  const total = rows.reduce((s, i) => s + (i.status === "PAID" || i.status === "WON" ? 0 : Number(i.amount || 0)), 0);
  return (
    <section data-testid={`money-${kind.toLowerCase()}`}>
      <SectionHeader
        code={kind === "OWED_TO_ME" ? "01" : kind === "INVOICE" ? "02" : "03"}
        label={KIND_LABELS[kind]}
        caption={`${rows.length} open · ${fmt(total)} in play`}
      />
      <div className="op-panel">
        {rows.length === 0 && (
          <div className="p-6 text-[#5E657D] text-sm">Nothing here right now.</div>
        )}
        {rows.map((r) => {
          const t = TONE_STYLE[tone(r)];
          const done = r.status === "PAID" || r.status === "WON";
          const days = daysUntil(r.due_date);
          return (
            <div
              key={r.id}
              data-testid={`money-row-${r.id}`}
              className={`grid grid-cols-12 gap-4 items-center px-6 py-4 border-l-4 ${t.border} ${t.bg} border-b border-white/[0.04] last:border-b-0 ${done ? "opacity-45" : ""}`}
            >
              <div className="col-span-12 md:col-span-5">
                <div className="op-tag mb-1">{r.business}</div>
                <div className={`text-white text-base ${done ? "line-through" : ""}`}>{r.label}</div>
                <div className="text-[#A0A5B5] text-xs mt-0.5">{r.party}</div>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="op-tag mb-1">AMOUNT</div>
                <div className={`op-mono text-sm ${t.text}`}>{fmt(r.amount)}</div>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="op-tag mb-1">DUE</div>
                <div className="text-white op-mono text-sm">
                  {r.due_date || "—"}
                  {days != null && (
                    <span className={`ml-2 text-[10px] ${t.text}`}>
                      {days < 0 ? `${-days}d over` : days === 0 ? "today" : `${days}d`}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="op-tag mb-1">STATUS</div>
                <span className={`op-mono text-[10px] tracking-[0.2em] ${t.text}`}>{r.status}</span>
              </div>
              <div className="col-span-12 md:col-span-1 flex md:justify-end">
                <button
                  onClick={() => onToggle(r)}
                  data-testid={`money-toggle-${r.id}`}
                  className="op-btn text-[9px] px-3 py-1.5 inline-flex items-center gap-2"
                  title={done ? "Reopen" : "Mark done"}
                >
                  <Check size={11} /> {done ? "REOPEN" : "DONE"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const NewMoneyForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: "OWED_TO_ME",
    label: "",
    party: "",
    business: "PYN CAPITAL",
    amount: "",
    due_date: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.label || !form.party || !form.amount) return;
    await api.createMoney({ ...form, amount: parseFloat(form.amount) });
    toast("Added");
    setForm({ ...form, label: "", party: "", amount: "" });
    setOpen(false);
    onCreated();
  };
  return open ? (
    <form onSubmit={submit} className="op-panel p-6 space-y-4 mb-8" data-testid="money-new-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="op-input op-mono text-xs">
          <option value="OWED_TO_ME">MONEY OWED TO ME</option>
          <option value="INVOICE">INVOICE OUT</option>
          <option value="DEAL_CLOSING">DEAL CLOSING</option>
        </select>
        <input placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="op-input" />
        <input placeholder="Party / client" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="op-input" />
        <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="op-input op-mono" />
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="op-input op-mono" />
        <select value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="op-input op-mono text-xs">
          {["PYN CAPITAL", "DOA / PRO PARTNER", "BIOVAULT", "PERSONAL"].map((b) => <option key={b}>{b}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="op-btn">CANCEL</button>
        <button type="submit" className="op-btn op-btn--primary" data-testid="money-new-submit">SAVE</button>
      </div>
    </form>
  ) : (
    <button
      onClick={() => setOpen(true)}
      data-testid="money-new"
      className="op-btn inline-flex items-center gap-2 mb-8"
    >
      <Plus size={12} /> NEW ENTRY
    </button>
  );
};

const Money = () => {
  const [items, setItems] = useState([]);
  const load = () => api.money().then(setItems);
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    const done = r.status === "PAID" || r.status === "WON";
    const nextStatus = done
      ? "OPEN"
      : r.kind === "DEAL_CLOSING"
      ? "WON"
      : "PAID";
    await api.patchMoney(r.id, { status: nextStatus });
    load();
  };

  const totals = React.useMemo(() => {
    const open = items.filter((i) => !(i.status === "PAID" || i.status === "WON" || i.status === "LOST"));
    return {
      owed: open.filter((i) => i.kind === "OWED_TO_ME").reduce((s, i) => s + Number(i.amount || 0), 0),
      out: open.filter((i) => i.kind === "INVOICE").reduce((s, i) => s + Number(i.amount || 0), 0),
      pipeline: open.filter((i) => i.kind === "DEAL_CLOSING").reduce((s, i) => s + Number(i.amount || 0), 0),
    };
  }, [items]);

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1600px] mx-auto space-y-12" data-testid="money-page">
      <header className="relative">
        <div className="op-microcopy text-[#00E5FF] mb-4">CASH FLOW · GLANCE</div>
        <h1 className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3">Money.</h1>
        <p className="text-[#A0A5B5] text-lg max-w-2xl">
          Green = clear · Amber = due this week · Red = overdue. Nothing hides.
        </p>
        <div className="op-hairline mt-8" />
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="money-summary">
        <div className="op-panel p-5 border-l-4 border-l-[#22c55e]">
          <div className="op-tag mb-1">OWED TO ME</div>
          <div className="text-white text-3xl font-light op-mono">{fmt(totals.owed)}</div>
        </div>
        <div className="op-panel p-5 border-l-4 border-l-[#F5A623]">
          <div className="op-tag mb-1">INVOICES OUT</div>
          <div className="text-white text-3xl font-light op-mono">{fmt(totals.out)}</div>
        </div>
        <div className="op-panel p-5 border-l-4 border-l-[#00E5FF]">
          <div className="op-tag mb-1">DEALS IN PLAY</div>
          <div className="text-white text-3xl font-light op-mono">{fmt(totals.pipeline)}</div>
        </div>
      </div>

      <NewMoneyForm onCreated={load} />

      <MoneyCandidates onConfirmed={load} />

      <MoneySection kind="OWED_TO_ME" items={items} onToggle={toggle} />
      <MoneySection kind="INVOICE" items={items} onToggle={toggle} />
      <MoneySection kind="DEAL_CLOSING" items={items} onToggle={toggle} />
    </div>
  );
};

export default Money;
